import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getHuscleosMrr } from '../../lib/api';
import { checkMilestones } from '../../utils/milestones';
import {
  TrendingUp, Users, Target, Zap, DollarSign, Flame, PhoneCall, CheckCircle2, X, Sparkles,
} from 'lucide-react';

/** Animated counter — ticks from previous value to new value */
function Counter({ value, prefix = '', suffix = '', decimals = 0, duration = 800 }) {
  const [display, setDisplay] = useState(value);
  const prev = useRef(value);
  const raf = useRef(null);

  useEffect(() => {
    const from = prev.current;
    const to = value;
    if (from === to) return;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(from + (to - from) * eased);
      if (t < 1) raf.current = requestAnimationFrame(tick);
      else { prev.current = to; setDisplay(to); }
    };
    raf.current = requestAnimationFrame(tick);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [value, duration]);

  const formatted = decimals > 0
    ? display.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : Math.round(display).toLocaleString('en-US');
  return <span>{prefix}{formatted}{suffix}</span>;
}

function Stat({ icon, label, value, sub, accent = '#FF2E00', testId, highlight = false }) {
  return (
    <div
      className="relative bg-white border border-gray-200 p-4 flex flex-col justify-between min-h-[100px] transition-all hover:border-gray-300 hover:shadow-sm"
      data-testid={testId}
    >
      {highlight && (
        <span className="absolute top-2.5 right-2.5 w-2 h-2 rounded-full animate-pulse" style={{ background: accent }} />
      )}
      <div className="flex items-center gap-2 mb-2">
        <div style={{ color: accent }}>{icon}</div>
        <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500" style={{ fontFamily: 'Manrope' }}>{label}</p>
      </div>
      <div>
        <p className="text-2xl font-bold leading-none tabular-nums" style={{ fontFamily: 'Manrope', color: '#0F0F0F' }}>{value}</p>
        {sub && <p className="text-[11px] text-gray-500 mt-1.5">{sub}</p>}
      </div>
    </div>
  );
}

export default function MRRStatsBar() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [milestone, setMilestone] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await getHuscleosMrr();
      setData(d);
      // Check for newly-crossed milestones (triggers confetti + banner)
      const crossed = checkMilestones({
        customers: d?.funnel?.converted || 0,
        mrr: d?.mrr?.closed || 0,
      });
      if (crossed) setMilestone(crossed);
    } catch (err) {
      // Dashboard stays functional on error — log so we can debug in the console
      console.error('[MRRStatsBar] failed to fetch HuscleOS MRR:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000); // live refresh
    return () => clearInterval(interval);
  }, [load]);

  if (loading && !data) {
    return (
      <div className="mb-6 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-gray-100 border border-gray-200 p-4 min-h-[100px] animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-gray-100 border border-gray-200 p-4 min-h-[100px] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const m = data?.mrr || {};
  const f = data?.funnel || {};
  const t = data?.tier_breakdown || {};

  return (
    <div className="mb-6 space-y-3" data-testid="mrr-stats-bar">

      {/* Milestone celebration banner */}
      {milestone && (
        <div
          className="relative bg-gradient-to-r from-[#0F0F0F] to-[#1F1F1F] border border-[#FF2E00] p-5 flex items-center gap-4 shadow-lg animate-[fadeInDown_0.4s_ease-out]"
          data-testid="milestone-banner"
        >
          <div className="flex items-center justify-center w-12 h-12 bg-[#FF2E00]/15 rounded-full shrink-0">
            <span className="text-2xl">{milestone.emoji}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={12} className="text-[#FF2E00]" />
              <p className="text-[10px] font-bold tracking-widest uppercase text-[#FF2E00]" style={{ fontFamily: 'Manrope' }}>
                Milestone Unlocked · {milestone.label}
              </p>
            </div>
            <p className="text-lg font-bold text-white leading-tight" style={{ fontFamily: 'Manrope' }}>
              {milestone.headline}
            </p>
          </div>
          <button
            onClick={() => setMilestone(null)}
            data-testid="milestone-dismiss"
            className="shrink-0 text-gray-500 hover:text-white transition-colors p-1"
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Row 1 — Revenue */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat
          icon={<DollarSign size={13} />}
          label="Closed MRR"
          value={<Counter value={m.closed || 0} prefix="$" />}
          sub={m.closed_this_month > 0 ? `+$${(m.closed_this_month || 0).toLocaleString()} this month` : 'waiting for first close'}
          accent="#10B981"
          testId="stat-closed-mrr"
          highlight={m.closed_this_month > 0}
        />
        <Stat
          icon={<TrendingUp size={13} />}
          label="ARR Projection"
          value={<Counter value={m.arr_projection || 0} prefix="$" />}
          sub="closed MRR × 12"
          accent="#10B981"
          testId="stat-arr"
        />
        <Stat
          icon={<Target size={13} />}
          label="Pipeline Value"
          value={<Counter value={m.pipeline_value || 0} prefix="$" />}
          sub={`${f.total_leads || 0} - ${f.converted || 0} - ${f.lost || 0} active at Grow`}
          accent="#FF2E00"
          testId="stat-pipeline-value"
        />
        <Stat
          icon={<Zap size={13} />}
          label="Avg Deal Size"
          value={<Counter value={m.avg_deal || 0} prefix="$" />}
          sub={f.converted ? `across ${f.converted} close${f.converted === 1 ? '' : 's'}` : 'no closes yet'}
          accent="#FF2E00"
          testId="stat-avg-deal"
        />
      </div>

      {/* Row 2 — Funnel */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat
          icon={<Users size={13} />}
          label="Total Leads"
          value={<Counter value={f.total_leads || 0} />}
          sub={f.new_this_week ? `+${f.new_this_week} this week` : 'no new leads this week'}
          accent="#6366F1"
          testId="stat-total-leads"
          highlight={f.new_this_week > 0}
        />
        <Stat
          icon={<PhoneCall size={13} />}
          label="Calls Booked"
          value={<Counter value={f.calls_booked || 0} />}
          sub={`${f.demo_ready || 0} demos delivered`}
          accent="#6366F1"
          testId="stat-calls-booked"
        />
        <Stat
          icon={<Flame size={13} />}
          label="Hot Leads"
          value={<Counter value={f.hot_leads || 0} />}
          sub="drip step 3+"
          accent="#F59E0B"
          testId="stat-hot-leads"
          highlight={f.hot_leads > 0}
        />
        <Stat
          icon={<CheckCircle2 size={13} />}
          label="Conversion Rate"
          value={<Counter value={f.conversion_rate_pct || 0} decimals={1} suffix="%" />}
          sub={`${f.converted || 0} closed · ${f.lost || 0} lost`}
          accent="#10B981"
          testId="stat-conversion-rate"
        />
      </div>

      {/* Tier breakdown strip */}
      {(t.launch > 0 || t.grow > 0 || t.dominate > 0) && (
        <div className="bg-white border border-gray-200 p-3 flex flex-wrap items-center gap-6" data-testid="stat-tier-breakdown">
          <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500" style={{ fontFamily: 'Manrope' }}>Closed by tier:</p>
          <div className="flex items-center gap-6 text-xs">
            <span className="text-gray-500">Launch <span className="font-bold tabular-nums" style={{ color: '#0F0F0F' }}>{t.launch}</span></span>
            <span className="text-gray-500">Grow <span className="text-[#FF2E00] font-bold tabular-nums">{t.grow}</span></span>
            <span className="text-gray-500">Dominate <span className="text-[#10B981] font-bold tabular-nums">{t.dominate}</span></span>
          </div>
          <span className="ml-auto text-[10px] text-gray-400">live · refreshes every 30s</span>
        </div>
      )}
    </div>
  );
}
